import { useEffect, useState } from 'react';
import { Facets } from './types';
import { CARD_TYPES } from './constants';

const DIGITAL_PREFIXES = ['digital', 'electronic', 'email', 'online', 'virtual'] as const;
const DIGITAL_SUFFIXES = ['invite', 'invitation', 'save the date'] as const;
const DIGITAL_TERMS = DIGITAL_PREFIXES.reduce(
  (acc, prefix) => {
    const prefixedTerms = DIGITAL_SUFFIXES.map((suffix) => `${prefix} ${suffix}`);
    return [...prefixedTerms, ...acc];
  },
  ['e-invite', 'e invite']
);

type CardTypeValues = keyof typeof CARD_TYPES;
const TERMS_BY_CARD_TYPE: { [key in CardTypeValues]?: string[] } = {
  [CARD_TYPES.invitation]: ['invite', 'invitation', 'rsvp', 'response card'],
  [CARD_TYPES.ctd]: ['change the date'],
  [CARD_TYPES.std]: ['save the date'],
  [CARD_TYPES.holiday]: ['holiday card', 'christmas card'],
  [CARD_TYPES.enclosure]: ['enclosure'],
  [CARD_TYPES.menu]: ['menu'],
  [CARD_TYPES.program]: ['program'],
  [CARD_TYPES.thankyou]: ['thank you'],
  [CARD_TYPES.place]: [
    'escort card',
    'name card',
    'place card',
    'placement card',
    'seating card',
    'table card',
    'placecard',
  ],
};

const MOTIF_TERMS = [
  'rustic',
  'simple',
  'beach',
  'formal',
  'destination',
  'modern',
  'vintage',
  'floral',
  'watercolor',
  'classic',
  'greenery',
  'bohemian',
  'summer',
  'winter',
  'spring',
  'fall',
];

type COLOR = { term: string; value: string };
const COLORS: COLOR[] = [
  { term: 'dusty blue', value: 'DUSTY_BLUE' },
  { term: 'royal blue', value: 'ROYAL_BLUE' },
  { term: 'blue', value: 'BLUE' },
  { term: 'navy', value: 'NAVY' },
  { term: 'black', value: 'BLACK' },
  { term: 'red', value: 'RED' },
  { term: 'burgundy', value: 'BURGUNDY' },
  { term: 'purple', value: 'PURPLE' },
  { term: 'lavender', value: 'LAVENDER' },
  { term: 'plum', value: 'PLUM' },
  { term: 'pink', value: 'PINK' },
  { term: 'dusty rose', value: 'DUSTY_ROSE' },
  { term: 'rose', value: 'ROSE' },
  { term: 'coral', value: 'CORAL' },
  { term: 'peach', value: 'PEACH' },
  { term: 'emerald green', value: 'EMERALD_GREEN' },
  { term: 'green', value: 'GREEN' },
  { term: 'teal', value: 'TEAL' },
  { term: 'orange', value: 'ORANGE' },
  { term: 'yellow', value: 'YELLOW' },
  { term: 'grey', value: 'GREY' },
  { term: 'gray', value: 'GREY' },
  { term: 'brown', value: 'BROWN' },
  { term: 'beige', value: 'BEIGE' },
  { term: 'off-white', value: 'BEIGE' },
  { term: 'off white', value: 'BEIGE' },
  { term: 'white', value: 'WHITE' },
];

const containsTermAsWord = (query: string, term: string) => new RegExp(`\\b${term}\\b`).test(query);

const getFacetsFromQueryString = (searchQuery: string): Facets | null => {
  const query = searchQuery.toLowerCase();

  let cardType;
  let isDigital = false;
  if (DIGITAL_TERMS.some((term) => query.includes(term))) {
    // If any digital STD terms are present in the query, choose STD as the card type (checked before others to avoid term overlap)
    cardType = CARD_TYPES.std;
    isDigital = true;
  } else {
    // Check if any other card types' terms are included in the query
    cardType = Object.keys(TERMS_BY_CARD_TYPE).find((type) => {
      const allTermsForType = TERMS_BY_CARD_TYPE[type as CardTypeValues];
      return allTermsForType?.some((term) => query.includes(term));
    });
  }

  // If no supported card type terms are found, we assume it's not a paper query
  if (!cardType) return null;

  // Extract all supported motifs from the query
  const motifs = MOTIF_TERMS.filter((term) => containsTermAsWord(query, term));

  // Extract the first supported color from the query. Only choose one to avoid overlaps like "off-white" and "white"
  const color = COLORS.find(({ term }) => containsTermAsWord(query, term))?.value;

  // Magnets, letterpress and foil are currently mutually exclusive
  const magnet = query.includes('magnet');
  const letterpress = !magnet && query.includes('letterpress');
  const foil = !magnet && !letterpress && containsTermAsWord(query, 'foil');

  return {
    lead_card_type: cardType,
    digital_suite: isDigital,
    motifs: motifs.length ? motifs : undefined,
    color: color ? [color] : undefined,
    has_magnet: magnet,
    has_foil: foil,
    is_letterpress: letterpress,
  };
};

export const usePaperFacets = (searchQuery: string): Facets | null => {
  const [facets, setFacets] = useState<Facets | null>(null);

  useEffect(() => {
    setFacets(searchQuery ? getFacetsFromQueryString(searchQuery) : null);
  }, [searchQuery]);

  return facets;
};
